import { useState, useEffect, useMemo, useCallback } from "react";

// react-router components
import { Route, Switch, Redirect, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// PorterIQ Dashboard React components
import VuiBox from "components/ui/VuiBox";

// PorterIQ Dashboard React example components
import Sidenav from "components/navigation/Sidenav";

// PorterIQ Dashboard React themes
import theme from "assets/theme";

// PorterIQ Dashboard React routes
import getRoutes from "routes";

// PorterIQ Dashboard React contexts
import { useUIController, setMiniSidenav, setFixedNavbar } from "context";
import QuickCatchupContext from "context/QuickCatchupContext";

// Auth Provider
import { AuthProvider } from "./context/AuthContext";

// Analytics Provider
import { AnalyticsProvider } from "./context/AnalyticsContext";

// Access Denied component
import AccessDenied from "components/AccessDenied";

// Import jwtService to check authentication
import { jwtService } from "./services";

import { PressProvider } from "./context/PressContext";
function DashboardShell() {
  const [controller, dispatch] = useUIController();
  const { miniSidenav, direction, layout, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();

  const [routes, setRoutes] = useState(() => getRoutes());

  useEffect(() => {
    const handleStorageChange = () => {
      setRoutes(getRoutes());
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const handleOnMouseEnterNav = useCallback(() => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  }, [miniSidenav, onMouseEnter, dispatch]);

  const handleOnMouseLeaveNav = useCallback(() => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  }, [onMouseEnter, dispatch]);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    setFixedNavbar(dispatch, false);
  }, [dispatch]);

  const renderRoutes = useCallback((allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return renderRoutes(route.collapse);
      }

      if (route.route) {
        if (route.secure) {
          return (
            <Route
              exact
              path={route.route}
              key={route.key}
              render={(props) => {
                if (!jwtService.isAuthenticated()) {
                  return <Redirect to="/authentication/sign-in" />;
                }

                if (route.requiredRole && !jwtService.hasRole(route.requiredRole)) {
                  return <AccessDenied />;
                }

                return <route.component {...props} />;
              }}
            />
          );
        } else {
          return <Route exact path={route.route} component={route.component} key={route.key} />;
        }
      }
      return null;
    }), []
  );

  return (
    <>
      <CssBaseline />
      <VuiBox
        sx={{
          minHeight: "100vh",
          backgroundColor: "transparent",
          backgroundImage: "none",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {layout === "dashboard" && (
          <Sidenav
            color={sidenavColor}
            brand=""
            brandName=""
            routes={routes}
            onMouseEnter={handleOnMouseEnterNav}
            onMouseLeave={handleOnMouseLeaveNav}
          />
        )}
        <VuiBox
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: { xs: "hidden", md: "visible" },
            position: "relative",
            backgroundColor: "transparent",
            backgroundImage: "none",
          }}
        >
          <Switch>
            {renderRoutes(routes)}
            <Redirect
              from="*"
              to={jwtService.isAuthenticated() ? "/press-digest" : "/authentication/sign-in"}
            />
          </Switch>
        </VuiBox>
      </VuiBox>
    </>
  );
}

function QuickCatchupBridge({ children }) {
  const [quickCatchupProps, setQuickCatchupProps] = useState({
    action: null,
    modal: null,
  });

  const setCatchup = useCallback((props) => {
    setQuickCatchupProps(props);
  }, []);

  const clearCatchup = useCallback(() => {
    setQuickCatchupProps({ action: null, modal: null });
  }, []);

  const quickCatchupValue = useMemo(() => ({
    setCatchup,
    clearCatchup,
    action: quickCatchupProps.action,
  }), [quickCatchupProps.action, setCatchup, clearCatchup]);

  return (
    <QuickCatchupContext.Provider value={quickCatchupValue}>
      {children}
      {quickCatchupProps.modal}
    </QuickCatchupContext.Provider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AnalyticsProvider>
        <PressProvider>
          <QuickCatchupBridge>
            <ThemeProvider theme={theme}>
              <DashboardShell />
            </ThemeProvider>
          </QuickCatchupBridge>
        </PressProvider>
      </AnalyticsProvider>
    </AuthProvider>
  );
}
